import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { LeaseDetailService } from '../../data/services/view/lease-detail.service';
import { BillingService } from '../../data/services/api/billing.service';
import { Billing, BillingFiscalSummary, CreateBillingDto, UpdateBillingDto } from '../../data/interfaces/billing.interface';
import { parseFolio } from '../../data/utils/folio.util';

@Component({
  selector: 'app-lease-billing',
  templateUrl: './lease-billing.component.html',
  styleUrls: ['./lease-billing.component.scss'],
  providers: [MessageService]
})
export class LeaseBillingComponent implements OnInit {
  
  billingInfo: Billing | null = null;
  isEditing = false;
  isLoading = false;
  
  constructor(
    private billingService: BillingService,
    private messageService: MessageService,
    public leaseDetailService: LeaseDetailService
  ) {}

  ngOnInit() {
    this.loadBillingInfo();
  }

  // La tab es SIEMPRE editable (patrón de las demás pestañas: Guardar/Actualizar).
  // El modo solo cambia la etiqueta del botón; create vs update se decide por billingInfo.
  get mode(): 'create' | 'edit' {
    return this.billingInfo ? 'edit' : 'create';
  }

  get data() {
    return this.leaseDetailService.data;
  }

  /**
   * Renta base del arrendamiento SIN IVA (sugerencia editable para el concepto "Renta").
   * Solo lectura de datos ya calculados; nunca recalcula (respeta el motor de cotización).
   */
  get suggestedRent(): number | null {
    const rent = this.leaseDetailService.getTermDataSafely()?.rent?.amount;
    return typeof rent === 'number' && rent > 0 ? rent : null;
  }

  /**
   * Datos fiscales derivados (read-only) que muestra el "Resumen fiscal" de la tab.
   * Razón Social ← cliente registrado · No. Contrato ← lease.contract · No. ID ← lease.oldId.
   */
  get fiscalSummary(): BillingFiscalSummary {
    const d = this.leaseDetailService.data;
    const client = d?.client;
    return {
      razonSocial: this.resolveRazonSocial(client),
      rfc: client?.rfc || '',
      contractNumber: d?.contract?.contractNumber || '',
      oldId: this.resolveNoId(d)
    };
  }

  /**
   * No. ID (col 22 del reporte) = primer dígito del folio `R-{n}-…` (el "oldId consolidado").
   * Se deriva del FOLIO, no de `lease.oldId`: al editar el folio, el backend guarda el folio ANTERIOR
   * en `oldId` (respaldo de trazabilidad), así que `oldId` deja de ser el número. Fallback a oldId
   * numérico por si el folio no es canónico. Regla espejo del backend (pending-invoices-excel.service).
   */
  private resolveNoId(d: any): string {
    const digit = parseFolio(d?.folio)?.currentDigit;
    if (digit) return digit;
    const oldId = String(d?.oldId ?? '');
    return /^\d+$/.test(oldId) ? oldId : '';
  }

  // Mismo criterio que customer-detail.getClientFullName: Moral→legalName; Física→nombre compuesto.
  private resolveRazonSocial(client: any): string {
    if (!client) return '';
    if (client.entity === 'Moral') return client.legalName || client.name || '';
    return [client.name, client.lastName, client.middleName].filter(Boolean).join(' ') || client.legalName || '';
  }

  /**
   * Cargar información de billing
   */
  loadBillingInfo() {
    this.isLoading = true;
    
    // Cargar desde LeaseDetailService
    this.leaseDetailService.loadBillingInfo();
    this.billingInfo = this.leaseDetailService.getBilling();
    
    this.isLoading = false;
  }

  /**
   * Manejar creación de billing
   */
  handleCreate(billingData: CreateBillingDto) {
    this.isLoading = true;
    
    this.billingService.createBilling(billingData).subscribe({
      next: (billing) => {
        this.billingInfo = billing;
        this.leaseDetailService.updateBillingInfo(billing);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Información de facturación creada correctamente'
        });
        
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        
        // Determinar tipo de error y mostrar mensaje apropiado
        if (error.message.includes('permisos')) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Permisos Insuficientes',
            detail: error.message,
            life: 6000
          });
        } else if (error.message.includes('validación')) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error de Validación',
            detail: error.message,
            life: 5000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Error al crear la información de facturación',
            life: 5000
          });
        }
      }
    });
  }

  /**
   * Manejar actualización de billing
   */
  handleUpdate(billingData: UpdateBillingDto) {
    if (!this.billingInfo) return;
    
    this.isLoading = true;
    
    this.billingService.updateBilling(this.billingInfo._id, billingData).subscribe({
      next: (billing) => {
        this.billingInfo = billing;
        this.leaseDetailService.updateBillingInfo(billing);
        this.isEditing = false;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Información de facturación actualizada correctamente'
        });
        
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        
        // Determinar tipo de error y mostrar mensaje apropiado
        if (error.message.includes('permisos')) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Permisos Insuficientes',
            detail: error.message,
            life: 6000
          });
        } else if (error.message.includes('validación')) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error de Validación',
            detail: error.message,
            life: 5000
          });
        } else if (error.message.includes('no fue encontrada')) {
          this.messageService.add({
            severity: 'error',
            summary: 'Registro No Encontrado',
            detail: error.message,
            life: 5000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Error al actualizar la información de facturación',
            life: 5000
          });
        }
      }
    });
  }

  /**
   * Manejar envío de formulario (unificado)
   */
  handleFormSubmit(billingData: CreateBillingDto | UpdateBillingDto) {
    if (this.billingInfo) {
      this.handleUpdate(billingData as UpdateBillingDto);
    } else {
      this.handleCreate(billingData as CreateBillingDto);
    }
  }

  /**
   * Manejar cancelación de formulario
   */
  handleCancel() {
    this.isEditing = false;
  }

  /**
   * Iniciar edición
   */
  startEdit() {
    this.isEditing = true;
  }

  /**
   * Obtener clase CSS para estado de billing
   */
  getBillingStatusClass(): string {
    if (!this.billingInfo) return 'status-missing';
    return 'status-configured';
  }

  /**
   * Obtener texto para estado de billing
   */
  getBillingStatusText(): string {
    if (!this.billingInfo) return 'Sin configurar';
    return 'Configurado';
  }
}
